import React, { useState } from "react";
import { cn } from "src/common/utils";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { useProductBranding } from "src/hooks/useProductBranding";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (tags: string[], text: string) => void;
  isSubmitting?: boolean;
}

const FEEDBACK_TAGS = [
  "Didn't find the right telemetry data",
  "Missing important context",
  "Incomplete investigation",
  "Incorrect conclusion",
  "Not following instructions",
  "Other",
] as const;

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  open,
  onOpenChange,
  onSubmit,
  isSubmitting = false,
}) => {
  const { productName } = useProductBranding();
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState("");

  const handleTagClick = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleSubmit = () => {
    onSubmit(selectedTags, feedbackText);
    // Reset form
    setSelectedTags([]);
    setFeedbackText("");
  };

  const canSubmit = selectedTags.length > 0 || feedbackText.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="large">
        <DialogHeader>
          <DialogTitle className="text-lg font-normal">
            Provide Feedback
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          {/* Quick feedback tags */}
          <div>
            <div className="flex flex-wrap gap-3">
              {FEEDBACK_TAGS.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className={cn(
                    "rounded-lg border px-3 py-1 text-sm transition-colors",
                    selectedTags.includes(tag)
                      ? "border-foreground/20 bg-accent text-foreground"
                      : "border-steel text-muted-foreground hover:text-foreground hover:bg-accent"
                  )}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Optional text input */}
          <div className="rounded-md border border-steel focus-within:border-foreground/30 focus-within:ring-1 focus-within:ring-foreground/20 px-3 py-2 shadow-none">
            <label htmlFor="feedback" className="block text-xs font-semibold text-muted-foreground mb-2">
              Additional details
            </label>
            <textarea
              id="feedback"
              aria-label="Feedback details"
              className="block w-full border-0 p-0 shadow-none outline-none resize-none bg-transparent text-foreground placeholder-neutral-500 sm:text-sm"
              placeholder={`Tell us what went wrong - your feedback helps ${productName} learn and improve.`}
              rows={4}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
            />
          </div>

          {/* Submit button */}
          <div className="flex w-full flex-row items-center justify-end">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
